(() => {
  const shopRoot = window.Shopify?.routes?.root || '/';
  let busy = false;
  let queuedRefresh = false;

  const getPage = () => document.querySelector('[data-tazty-cart-page]');
  const getSectionId = () => getPage()?.dataset.sectionId;

  const setStatus = message => {
    const status = getPage()?.querySelector('[data-cart-status]');
    if (status) status.textContent = message || '';
  };

  const setBusy = value => {
    busy = value;
    const page = getPage();
    page?.setAttribute('aria-busy', String(value));
    page?.querySelectorAll('[data-cart-step], [data-cart-quantity], [data-cart-remove], [data-cart-clear], [name="checkout"]')
      .forEach(control => { control.disabled = value; });
  };

  const updateHeader = count => {
    document.querySelectorAll('.site-header__cart').forEach(link => {
      const badge = link.querySelector('b');
      if (badge) badge.textContent = count;
      link.setAttribute('aria-label', `Cart, ${count} items`);
    });
  };

  const updateShipping = () => {
    const page = getPage();
    if (!page) return;
    window.TaztyShipping?.update(
      page.querySelector('[data-shipping-bar]'),
      Number(page.dataset.cartTotal),
      page.dataset.cartCurrency || window.Shopify?.currency?.active || 'USD'
    );
  };

  const replacePage = html => {
    if (typeof html !== 'string') throw new Error('The cart display could not be refreshed. Reload the page and try again.');
    const incoming = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-tazty-cart-page]');
    const current = getPage();
    if (!incoming || !current) throw new Error('The cart display could not be refreshed. Reload the page and try again.');
    current.replaceWith(incoming);
    if (incoming.dataset.cartCount != null) updateHeader(Number(incoming.dataset.cartCount));
    updateShipping();
    incoming.dispatchEvent(new CustomEvent('tazty:products:loaded', { bubbles: true }));
  };

  const renderedSection = cart => {
    const sectionId = getSectionId();
    return sectionId && cart?.sections ? cart.sections[sectionId] : null;
  };

  async function refresh() {
    const page = getPage();
    const sectionId = page?.dataset.sectionId;
    if (!page || !sectionId) return;
    if (busy) { queuedRefresh = true; return; }
    setBusy(true);
    setStatus('');
    try {
      let warning = '';
      let sections;
      try { sections = (await window.TaztyPackDiscounts?.sync(null, { sections: [sectionId] }))?.sections; }
      catch (_) { warning = 'The pack discount could not be confirmed. Reload your cart to check it before checkout.'; }
      if (typeof sections?.[sectionId] !== 'string') {
        const url = new URL(window.location.href);
        url.searchParams.set('sections', sectionId);
        const response = await fetch(url.toString(), { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(15000) });
        if (!response.ok) throw new Error('The cart could not be refreshed.');
        sections = await response.json();
      }
      replacePage(sections[sectionId]);
      if (warning) setStatus(warning);
    } catch (error) {
      setStatus(error.name === 'TimeoutError' ? 'The cart update took too long. Reload the page to confirm it.' : error.message);
    } finally {
      setBusy(false);
      if (queuedRefresh) { queuedRefresh = false; refresh(); }
    }
  }

  async function mutate(endpoint, body, fallbackMessage) {
    const page = getPage();
    const sectionId = page?.dataset.sectionId;
    if (!page || !sectionId || busy) return;
    setBusy(true);
    setStatus('');
    try {
      const response = await fetch(`${shopRoot}cart/${endpoint}.js`, {
        method: 'POST',
        credentials: 'same-origin',
        signal: AbortSignal.timeout(15000),
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...body, sections: [sectionId], sections_url: window.location.pathname })
      });
      let cart = await response.json();
      if (!response.ok) throw new Error(cart.description || cart.message || fallbackMessage);
      let warning = '';
      try { cart = await window.TaztyPackDiscounts?.sync(cart, { sections: [sectionId] }) || cart; }
      catch (_) { warning = 'Your cart was updated, but the pack discount could not be confirmed. Reload to check the total before checkout.'; }
      updateHeader(Number(cart.item_count) || 0);
      const sectionHtml = renderedSection(cart);
      if (sectionHtml) replacePage(sectionHtml);
      else queuedRefresh = true;
      if (warning) setStatus(warning);
    } catch (error) {
      setStatus(error.name === 'TimeoutError'
        ? 'The update could not be confirmed. Reload the page before trying again.'
        : (error.message || fallbackMessage));
      getPage()?.querySelectorAll('[data-cart-quantity]').forEach(input => { input.value = input.defaultValue; });
    } finally {
      setBusy(false);
      if (queuedRefresh) { queuedRefresh = false; refresh(); }
    }
  }

  async function splitPackIncrease(line, quantity) {
    const page = getPage();
    const sectionId = page?.dataset.sectionId;
    const key = line?.dataset.cartKey;
    const variantId = Number(line?.dataset.cartVariantId);
    const discountedQuantity = Number(line?.dataset.cartDiscountedQuantity);
    const input = line?.querySelector('[data-cart-quantity]');
    const currentQuantity = Number(input?.defaultValue);
    if (!page || !sectionId || busy || !key || !Number.isSafeInteger(variantId) || variantId <= 0
      || !Number.isSafeInteger(discountedQuantity) || discountedQuantity <= 0
      || !Number.isSafeInteger(quantity) || quantity <= discountedQuantity) return;
    setBusy(true);
    setStatus('');
    try {
      // The pack selected on the product page remains discounted. Any quantity
      // added later in the cart is a separate line without pack properties.
      if (currentQuantity !== discountedQuantity) {
        const changeResponse = await fetch(`${shopRoot}cart/change.js`, {
          method: 'POST', credentials: 'same-origin', signal: AbortSignal.timeout(15000),
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity: discountedQuantity })
        });
        const changedCart = await changeResponse.json();
        if (!changeResponse.ok) throw new Error(changedCart.description || changedCart.message || 'This quantity is unavailable.');
      }
      const response = await fetch(`${shopRoot}cart/add.js`, {
        method: 'POST', credentials: 'same-origin', signal: AbortSignal.timeout(15000),
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: quantity - discountedQuantity }],
          sections: [sectionId], sections_url: window.location.pathname
        })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || 'This quantity is unavailable.');
      if (result.sections?.[sectionId]) replacePage(result.sections[sectionId]);
      else queuedRefresh = true;
    } catch (error) {
      setStatus(error.name === 'TimeoutError'
        ? 'The update could not be confirmed. Reload the page before trying again.'
        : (error.message || 'This quantity is unavailable.'));
      getPage()?.querySelectorAll('[data-cart-quantity]').forEach(quantityInput => { quantityInput.value = quantityInput.defaultValue; });
    } finally {
      setBusy(false);
      if (queuedRefresh) { queuedRefresh = false; refresh(); }
    }
  }

  const validQuantity = input => {
    const quantity = Number(input.value);
    return Number.isSafeInteger(quantity) && quantity >= 0 ? quantity : null;
  };
  const maximumFor = input => {
    const maximum = Number(input?.max);
    return input?.max == null || input.max === '' || !Number.isFinite(maximum) ? Infinity : maximum;
  };

  document.addEventListener('click', event => {
    const page = getPage();
    if (!page) return;

    const step = event.target.closest('[data-cart-step]');
    if (step && page.contains(step)) {
      event.preventDefault();
      const line = step.closest('[data-cart-line]');
      const input = line?.querySelector('[data-cart-quantity]');
      if (!line || !input) return;
      const increment = Math.max(1, Number(input.step) || 1);
      const maximum = maximumFor(input);
      const next = Math.max(0, Number(input.value) + Number(step.dataset.cartStep) * increment);
      if (next > maximum) { setStatus(`The maximum available quantity is ${maximum}.`); return; }
      input.value = next;
      const discountedQuantity = Number(line.dataset.cartDiscountedQuantity || 0);
      if (!window.TaztyPackDiscounts?.enabled && discountedQuantity > 0 && next > discountedQuantity) splitPackIncrease(line, next);
      else mutate('change', { id: line.dataset.cartKey, quantity: next }, 'This quantity is unavailable.');
      return;
    }

    const remove = event.target.closest('[data-cart-remove]');
    if (remove && page.contains(remove)) {
      event.preventDefault();
      const line = remove.closest('[data-cart-line]');
      if (line) mutate('change', { id: line.dataset.cartKey, quantity: 0 }, 'This item could not be removed.');
      return;
    }

    const clear = event.target.closest('[data-cart-clear]');
    if (clear && page.contains(clear)) {
      event.preventDefault();
      mutate('clear', {}, 'The cart could not be cleared.');
    }
  });

  document.addEventListener('change', event => {
    const input = event.target.closest('[data-cart-quantity]');
    const page = getPage();
    if (!input || !page?.contains(input)) return;
    const quantity = validQuantity(input);
    if (quantity === null) {
      input.value = input.defaultValue;
      setStatus('Enter a whole quantity of zero or more.');
      return;
    }
    const maximum = maximumFor(input);
    if (quantity > maximum) {
      input.value = input.defaultValue;
      setStatus(`The maximum available quantity is ${maximum}.`);
      return;
    }
    const line = input.closest('[data-cart-line]');
    const discountedQuantity = Number(line.dataset.cartDiscountedQuantity || 0);
    if (!window.TaztyPackDiscounts?.enabled && discountedQuantity > 0 && quantity > discountedQuantity) splitPackIncrease(line, quantity);
    else mutate('change', { id: line.dataset.cartKey, quantity }, 'This quantity is unavailable.');
  });

  document.addEventListener('submit', event => {
    const form = event.target.closest('[data-cart-discount-form]');
    const page = getPage();
    if (!form || !page?.contains(form)) return;
    event.preventDefault();
    const code = String(new FormData(form).get('discount') || '').trim();
    if (!code) { form.querySelector('input')?.focus(); return; }
    window.location.assign(`${shopRoot}discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(`${shopRoot}cart`)}`);
  });

  document.addEventListener('tazty:cart-updated', event => {
    const html = event.detail?.sections?.[getSectionId()];
    if (getPage() && !busy && typeof html === 'string') {
      try { replacePage(html); return; } catch (_) { /* Fetch a fresh section below. */ }
    }
    if (getPage() && !busy) refresh();
  });
  document.addEventListener('shopify:section:load', event => {
    if (event.target.querySelector?.('[data-tazty-cart-page]')) updateShipping();
  });
  ['currency:change', 'shopify:currency:change', 'localization:change'].forEach(name =>
    document.addEventListener(name, () => { if (getPage()) refresh(); }));
  window.addEventListener('pageshow', updateShipping);
  const boot = () => {
    updateShipping();
    if (window.TaztyPackDiscounts?.enabled && getPage()) refresh();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
